import { useEffect, useRef, useState } from "react";
import type { Movie } from "@/types/movie";

interface PlayerDemoProps {
  movie: Movie;
}

type PlayerStatus = "idle" | "playing" | "error";

export default function PlayerDemo({ movie }: PlayerDemoProps) {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setStatus("idle");
    setProgress(0);
  }, [movie.id]);

  useEffect(() => {
    if (status !== "playing") {
      if (timerRef.current) window.clearInterval(timerRef.current);
      return;
    }
    timerRef.current = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 100 : prev + 0.5));
    }, 200);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [status]);

  return (
    <div>
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title}播放画面`}
          title={`${movie.title} 在线播放`}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-foreground-950/55"></div>

        {status === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <button
              type="button"
              onClick={() => setStatus("playing")}
              className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500 hover:bg-accent-600 text-background-50 cursor-pointer transition-transform hover:scale-105"
              aria-label="播放"
            >
              <i className="ri-play-fill text-3xl md:text-4xl" aria-hidden="true"></i>
            </button>
            <p className="mt-4 text-background-50 font-heading text-lg md:text-xl font-bold">
              {movie.title}
            </p>
            <p className="mt-1 text-background-200 text-xs md:text-sm">
              高清在线播放 · {movie.duration}
            </p>
          </div>
        )}

        {status === "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <span className="flex items-center gap-2 text-accent-400 text-sm font-semibold mb-3">
              <i className="ri-film-line text-lg animate-pulse" aria-hidden="true"></i>
              前端演示播放中
            </span>
            <p className="text-background-50 font-heading text-lg md:text-2xl font-bold mb-2">
              {movie.title}
            </p>
            <p className="text-background-200 text-xs md:text-sm max-w-md">
              本页面为界面演示，播放区域不提供真实音视频内容。实际观看请以正式上线版本为准。
            </p>
            <div className="mt-5 w-full max-w-md">
              <div className="h-1.5 w-full rounded-full bg-background-50/25 overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent-500 transition-all duration-200"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-background-200">
                <span>演示进度</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-50/15 text-accent-400 mb-4">
              <i className="ri-error-warning-line text-3xl" aria-hidden="true"></i>
            </span>
            <p className="text-background-50 font-semibold">播放失败，请稍后重试</p>
            <button
              type="button"
              onClick={() => {
                setProgress(0);
                setStatus("playing");
              }}
              className="mt-4 inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重新播放
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-400">
        <span className="flex items-center gap-1.5">
          <i className="ri-information-line text-base text-accent-500" aria-hidden="true"></i>
          播放区域为前端演示，不含真实音视频
        </span>
        {status !== "idle" && (
          <button
            type="button"
            onClick={() => setStatus("error")}
            className="flex items-center gap-1.5 text-foreground-400 hover:text-accent-700 cursor-pointer transition-colors"
          >
            <i className="ri-bug-line text-base" aria-hidden="true"></i>
            模拟播放异常
          </button>
        )}
      </div>
    </div>
  );
}